/**
 * Real-use header for personal environments (epic
 * 2026-10-23-personal-dev-envs-and-account-cleanup, sub-task 02).
 *
 * The nightly reaper stops a personal stage nobody USED in the last hour, and
 * raw API requests cannot tell: an idle tab keeps polling (media cookie
 * renewal, the imports list every 3 s while a row is unsettled, AI Review run
 * polling). So on a personal build only, a request to the stage's own API made
 * within 60 s of a click, a keystroke or a back/forward navigation carries
 * `X-Nu-User-Active: 1`; the API counts those (`app/auth/user_activity.py`).
 * In-app navigations (React Router) always follow a click or a key, so the
 * pointer and key listeners cover them.
 *
 * Installed by `main.tsx` through ONE global `fetch` wrapper, because every
 * client (`makeAuthedFetch` and the rest) calls the global `fetch` at request
 * time. Requests to any other origin (nu-advisory, Google, S3) are untouched.
 *
 * `installUserActivityHeader` is a no-op unless `VITE_PERSONAL_STAGE` is set,
 * which only `pnpm env:deploy`'s build does: the dev and prod builds never send
 * the header (`userActivity.test.ts` and `scripts/personal-env/lib.test.ts`).
 */

export const USER_ACTIVITY_HEADER = "X-Nu-User-Active";
export const ACTIVE_WINDOW_MS = 60_000;

/** The events that count as a person using the page. */
export const ACTIVITY_EVENTS = ["pointerdown", "keydown", "popstate"] as const;

export interface ActivityClock {
  mark: () => void;
  isActive: () => boolean;
}

export function createActivityClock(now: () => number = Date.now): ActivityClock {
  let last: number | undefined;
  return {
    mark: () => {
      last = now();
    },
    isActive: () => last !== undefined && now() - last < ACTIVE_WINDOW_MS,
  };
}

type FetchFn = typeof fetch;

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

/** `fetchImpl`, plus the header on requests to `apiOrigin` while `isActive()`. */
export function withUserActivityHeader(
  fetchImpl: FetchFn,
  { apiOrigin, isActive }: { apiOrigin: string; isActive: () => boolean }
): FetchFn {
  return (input, init) => {
    let origin: string;
    try {
      origin = new URL(requestUrl(input), apiOrigin).origin;
    } catch {
      return fetchImpl(input, init);
    }
    if (origin !== apiOrigin || !isActive()) return fetchImpl(input, init);
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined)
    );
    headers.set(USER_ACTIVITY_HEADER, "1");
    return fetchImpl(input, { ...init, headers });
  };
}

export interface InstallTarget {
  fetch: FetchFn;
  addEventListener: (type: string, listener: () => void, options?: AddEventListenerOptions) => void;
}

/**
 * Wire the header on a personal build. Returns whether it did: `false` (and
 * nothing touched) when `personalStage` or `apiUrl` is empty.
 */
export function installUserActivityHeader({
  personalStage,
  apiUrl,
  target,
  now,
}: {
  personalStage: string | undefined;
  apiUrl: string | undefined;
  target: InstallTarget;
  now?: () => number;
}): boolean {
  if (!personalStage || !apiUrl) return false;
  let apiOrigin: string;
  try {
    apiOrigin = new URL(apiUrl).origin;
  } catch {
    return false;
  }
  const clock = createActivityClock(now);
  for (const type of ACTIVITY_EVENTS) {
    target.addEventListener(type, clock.mark, { capture: true, passive: true });
  }
  target.fetch = withUserActivityHeader(target.fetch.bind(target), {
    apiOrigin,
    isActive: clock.isActive,
  });
  return true;
}
